import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Chargement, EnTetePage, Etiquette, Message } from "@cabinet/charte";
import { administrationApi, ErreurApi } from "../api/client";
import { formaterNombre } from "../domaine/formats";

const message = (echec: unknown) => (echec instanceof ErreurApi ? echec.message : "L'opération n'a pas abouti.");

/**
 * Console de la plateforme (EF-0109) : ouvrir un tenant avec son premier
 * administrateur, fixer son quota de dossiers, le suspendre. L'administrateur
 * de la plateforme n'entre jamais dans les dossiers d'un tenant.
 */
export function PagePlateforme() {
  const cache = useQueryClient();
  const tenants = useQuery({ queryKey: ["plateforme-tenants"], queryFn: administrationApi.tenants });
  const [saisie, setSaisie] = useState({
    raisonSociale: "",
    type: "CABINET",
    pays: "MA",
    quota: "",
    courriel: "",
    nom: "",
    motDePasse: "",
  });
  const [retour, setRetour] = useState<{ type: "succes" | "erreur"; texte: string } | null>(null);

  const rafraichir = () => void cache.invalidateQueries({ queryKey: ["plateforme-tenants"] });
  const ouverture = useMutation({
    mutationFn: () =>
      administrationApi.ouvrirTenant({
        raisonSociale: saisie.raisonSociale,
        type: saisie.type,
        pays: saisie.pays,
        quotaDeDossiers: saisie.quota === "" ? null : Number(saisie.quota),
        courrielDeLAdministrateur: saisie.courriel,
        nomDeLAdministrateur: saisie.nom,
        motDePasseDeLAdministrateur: saisie.motDePasse || null,
      }),
    onSuccess: () => {
      rafraichir();
      setRetour({ type: "succes", texte: `Tenant ${saisie.raisonSociale} ouvert, ${saisie.courriel} en est l'administrateur.` });
      setSaisie({ ...saisie, raisonSociale: "", courriel: "", nom: "", motDePasse: "", quota: "" });
    },
    onError: (echec) => setRetour({ type: "erreur", texte: message(echec) }),
  });
  const action = useMutation({
    mutationFn: (faire: () => Promise<unknown>) => faire(),
    onSuccess: rafraichir,
    onError: (echec) => setRetour({ type: "erreur", texte: message(echec) }),
  });

  const champ = (nom: keyof typeof saisie, libelle: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <label className="champ">
      {libelle}
      <input value={saisie[nom]} onChange={(e) => setSaisie({ ...saisie, [nom]: e.target.value })} {...props} />
    </label>
  );

  return (
    <>
      <EnTetePage
        titre="Console de la plateforme"
        description="Les tenants abonnés, leurs quotas de dossiers, leur suspension. L'administration de la plateforme n'entre jamais dans les dossiers d'un tenant."
      />

      {retour != null && <Message type={retour.type}>{retour.texte}</Message>}

      <form
        className="carte"
        onSubmit={(e) => {
          e.preventDefault();
          setRetour(null);
          ouverture.mutate();
        }}
      >
        <h3>Ouvrir un tenant</h3>
        <div className="champs-en-ligne">
          {champ("raisonSociale", "Raison sociale", { required: true })}
          <label className="champ">
            Type
            <select value={saisie.type} onChange={(e) => setSaisie({ ...saisie, type: e.target.value })}>
              <option value="CABINET">Cabinet</option>
              <option value="ENTREPRISE">Entreprise</option>
            </select>
          </label>
          {champ("pays", "Pays", { size: 3, maxLength: 2, required: true })}
          {champ("quota", "Quota de dossiers", { size: 6, inputMode: "numeric", placeholder: "illimité" })}
        </div>
        <div className="champs-en-ligne">
          {champ("courriel", "Courriel de l'administrateur", { type: "email", required: true })}
          {champ("nom", "Nom", { required: true })}
          {champ("motDePasse", "Mot de passe initial", { type: "password", autoComplete: "new-password", placeholder: "si nouveau compte" })}
          <button type="submit" className="principal" disabled={ouverture.isPending}>
            Ouvrir le tenant
          </button>
        </div>
      </form>

      {tenants.isPending && <Chargement quoi="les tenants" />}
      {tenants.error != null && <Message type="erreur">{message(tenants.error)}</Message>}
      {tenants.data != null && (
        <div className="tableau-conteneur">
          <table>
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Type</th>
                <th>Pays</th>
                <th className="nombre">Utilisateurs</th>
                <th className="nombre">Quota de dossiers</th>
                <th>Statut</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tenants.data.map((t) => (
                <tr key={t.id}>
                  <td>{t.raisonSociale}</td>
                  <td>{t.type}</td>
                  <td>{t.pays}</td>
                  <td className="nombre">{formaterNombre(t.nombreDUtilisateurs)}</td>
                  <td className="nombre">
                    {t.quotaDeDossiers ?? "illimité"}{" "}
                    <button
                      type="button"
                      className="tertiaire"
                      onClick={() => {
                        const valeur = window.prompt("Quota de dossiers (vide : illimité) :", t.quotaDeDossiers?.toString() ?? "");
                        if (valeur == null) return;
                        action.mutate(() => administrationApi.changerLeQuota(t.id, valeur.trim() === "" ? null : Number(valeur)));
                      }}
                    >
                      Modifier
                    </button>
                  </td>
                  <td>
                    <Etiquette variante={t.statut === "ACTIF" ? "succes" : "bloquant"}>
                      {t.statut === "ACTIF" ? "Actif" : "Suspendu"}
                    </Etiquette>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="tertiaire"
                      onClick={() =>
                        window.confirm(`${t.statut === "ACTIF" ? "Suspendre" : "Réactiver"} ${t.raisonSociale} ?`) &&
                        action.mutate(() => administrationApi.changerLeStatut(t.id, t.statut === "ACTIF" ? "SUSPENDU" : "ACTIF"))
                      }
                    >
                      {t.statut === "ACTIF" ? "Suspendre" : "Réactiver"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
